/**
 * Feature flags controlados via variáveis de ambiente Vite (VITE_SYNAP_*).
 * Flags opt-in usam default false; kill-switches estratégicos RW-S* usam default-on
 * (desligar com `VITE_SYNAP_*=false`).
 *
 * Rollout UX/UI (audit 2026-07): fases P1–P7 em `UX_ROLLOUT_*`; kill-switches
 * estratégicos (RW-S*) abaixo. Ver `docs/internal/UX_ROLLOUT_FLAGS.md`.
 */

function synapEnvFlag(value: string | undefined): boolean {
  return value === 'true'
}

/** Kill-switch default-on: habilitado salvo env explícito `false`. */
function synapEnvFlagDefaultOn(value: string | undefined): boolean {
  return value !== 'false'
}

/**
 * Quando true, o AnamnesisRealtimeAssistant envia `patientId` na API call de análise incremental,
 * habilitando a contextualização clínica via PatientClinicalContextBuilder na Edge Function.
 * Default: false (opt-in explícito por ambiente).
 */
export const ANAMNESIS_CLINICAL_CONTEXT_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_ANAMNESIS_CLINICAL_CONTEXT_ENABLED,
)

/**
 * Quando true, o campo de upload de imagem na tela de atendimento fica visível,
 * permitindo que o médico anexe imagem da queixa para análise multimodal via Gemini.
 * Default: false (opt-in explícito por ambiente).
 */
export const ENCOUNTER_CONDUCT_AI_MULTIMODAL_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_ENCOUNTER_CONDUCT_AI_MULTIMODAL_ENABLED,
)

// --- Audit kill-switches estratégicos (RW-S01–S04) ---

/** RW-S01 — re-criptografia at-rest + rotação BYOK (SEC-011). Default on; `=false` desliga. */
export const STORAGE_REENCRYPT_V1: boolean = synapEnvFlagDefaultOn(
  import.meta.env.VITE_SYNAP_STORAGE_REENCRYPT_V1,
)

/** RW-S03 — primitivo ListRow + migração virtualizada de cadastros (CON-005). Default on. */
export const LIST_ROW_PRIMITIVE_V1: boolean = synapEnvFlagDefaultOn(
  import.meta.env.VITE_SYNAP_LIST_ROW_PRIMITIVE_V1,
)

/** RW-S04 — janela de mensagens + lifecycle ChatIaPage (LEAK-009/010). Default on. */
export const CHAT_IA_LIFECYCLE_V1: boolean = synapEnvFlagDefaultOn(
  import.meta.env.VITE_SYNAP_CHAT_IA_LIFECYCLE_V1,
)

/** RW-S02 — back hierárquico nativo via routeParentMap (NAV-017). Default on. */
export const NAV_HIERARCHICAL_BACK_V1: boolean = synapEnvFlagDefaultOn(
  import.meta.env.VITE_SYNAP_NAV_HIERARCHICAL_BACK_V1,
)

/**
 * BACK-* (sketch 010, F0) — pilha única de overlays (`useOverlay`/`overlayManager`): voltar e Esc
 * fecham só o overlay do topo. Default on; `=false` volta ao caminho legado (push/popstate por
 * overlay + pilha nativa). Remover após 2 versões estáveis.
 */
export const OVERLAY_STACK_V2: boolean = synapEnvFlagDefaultOn(
  import.meta.env.VITE_SYNAP_OVERLAY_STACK_V2,
)

// --- Rollout UX/UI — fases P1–P7 (audit/19-rollout.md) ---
// Nota: remediações RW-Q/M deste PR são incondicionais; estas flags são telemetria/cohort futuro.

/** P1 — Token hygiene foundation (Quick Wins). */
export const UX_TOKEN_HYGIENE_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_UX_TOKEN_HYGIENE_ENABLED,
)

/** P2 — A11Y foreground & links (Quick Wins). */
export const UX_A11Y_FOREGROUND_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_UX_A11Y_FOREGROUND_ENABLED,
)

/** P3 — Safety alerts & confirms (Medium / safety). */
export const UX_SAFETY_CONFIRMS_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_UX_SAFETY_CONFIRMS_ENABLED,
)

/** P4 — Empty & loading parity (Quick Wins). */
export const UX_EMPTY_LOADING_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_UX_EMPTY_LOADING_ENABLED,
)

/** P5 — Critical clinical surfaces (Medium / speed). */
export const UX_CLINICAL_SURFACES_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_UX_CLINICAL_SURFACES_ENABLED,
)

/** P6 — Header & auth shell unification (Medium / trust). */
export const UX_SHELL_UNIFICATION_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_UX_SHELL_UNIFICATION_ENABLED,
)

/** P7 — Primitives, brand & list rows (Strategic). */
export const UX_PRIMITIVE_V2_ENABLED: boolean = synapEnvFlag(
  import.meta.env.VITE_SYNAP_UX_PRIMITIVE_V2_ENABLED,
)

export type UxRolloutPhaseId = 'P0' | 'P1' | 'P2' | 'P3' | 'P4' | 'P5' | 'P6' | 'P7'

/** Metadados de fase para telemetria e documentação (P0 não tem flag). */
export const UX_ROLLOUT_PHASES: Readonly<
  Record<
    UxRolloutPhaseId,
    {
      name: string
      flagEnv: string | null
      enabled: boolean | null
    }
  >
> = {
  P0: {
    name: 'Data protection containment',
    flagEnv: null,
    enabled: null,
  },
  P1: {
    name: 'Token hygiene foundation',
    flagEnv: 'VITE_SYNAP_UX_TOKEN_HYGIENE_ENABLED',
    enabled: UX_TOKEN_HYGIENE_ENABLED,
  },
  P2: {
    name: 'A11Y foreground & links',
    flagEnv: 'VITE_SYNAP_UX_A11Y_FOREGROUND_ENABLED',
    enabled: UX_A11Y_FOREGROUND_ENABLED,
  },
  P3: {
    name: 'Safety alerts & confirms',
    flagEnv: 'VITE_SYNAP_UX_SAFETY_CONFIRMS_ENABLED',
    enabled: UX_SAFETY_CONFIRMS_ENABLED,
  },
  P4: {
    name: 'Empty & loading parity',
    flagEnv: 'VITE_SYNAP_UX_EMPTY_LOADING_ENABLED',
    enabled: UX_EMPTY_LOADING_ENABLED,
  },
  P5: {
    name: 'Critical clinical surfaces',
    flagEnv: 'VITE_SYNAP_UX_CLINICAL_SURFACES_ENABLED',
    enabled: UX_CLINICAL_SURFACES_ENABLED,
  },
  P6: {
    name: 'Header & auth shell unification',
    flagEnv: 'VITE_SYNAP_UX_SHELL_UNIFICATION_ENABLED',
    enabled: UX_SHELL_UNIFICATION_ENABLED,
  },
  P7: {
    name: 'Primitives, brand & list rows',
    flagEnv: 'VITE_SYNAP_UX_PRIMITIVE_V2_ENABLED',
    enabled: UX_PRIMITIVE_V2_ENABLED,
  },
}
